// Flameco UI kit v2.8.0, copied by authservice ui-kit/sync.js. Do not edit; change the kit and re-sync.
'use client';
// The shared Flameco frame, in the look of Flameco World's home: the bar
// (FLAMECO lockup | APP NAME, "Search everything", app switcher, light/dark,
// account) and the side nav. Plain React + flameco-ui.css, so it looks the
// same in MUI, Tailwind/shadcn and plain-CSS apps. See STANDARD.md.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FLAMECO_APPS, FlamecoApp, FlamecoAppKey, resolveApps, worldProfileUrl } from './apps';
import { DougConfig, DougWidget } from './Doug';
import { GlobalSearch, GlobalSearchConfig, SearchButton, useSearchShortcut } from './GlobalSearch';
import { APP_ICONS, IconGrid, IconLogOut, IconMenu, IconMoon, IconSun, IconUser } from './icons';
import { LOCKUP_SVG, LOCKUP_VIEWBOX, MARK_SVG, MARK_VIEWBOX } from './logo';
import { FlamecoTheme, getTheme, initTheme, onThemeChange, setTheme } from './theme';

export interface FlamecoFrameUser {
  name: string;
  role?: string | null;
  employeeId?: string | null;
}

export interface FlamecoNavItem {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  /** Highlight; default: href matches currentPath. */
  active?: boolean;
}

export interface FlamecoNavGroup {
  section?: string;
  /** Category colour (OKLCH hue) for the group label; null = neutral (graphite). */
  categoryHue?: number | null;
  items: FlamecoNavItem[];
}

export interface FlamecoLinkProps {
  href: string;
  className: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  'aria-current'?: 'page' | 'true';
}

export interface FlamecoFrameProps {
  /** Which app this is (the name after the FLAMECO lockup, the switcher highlight). */
  app: FlamecoAppKey;
  user: FlamecoFrameUser | null;
  /** Signs out of every Flameco app (call flameco-auth signOut({ everywhere: true })). */
  onSignOut: () => void;
  /** Side nav. Omit for apps whose home is a tile grid with no nav. */
  nav?: FlamecoNavGroup[];
  /** Current route path, used to highlight the nav item. */
  currentPath?: string;
  /** Router-aware link (Next <Link>, react-router <Link>); default is <a>. */
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
  /** In-app navigation for search results on this app (router push). */
  navigate?: (href: string) => void;
  /** Where the logo goes (router link), e.g. the app's own home. Default: Flameco World's home, the parent of every app. */
  homeHref?: string;
  /** "Search everything": the backend that serves /flameco-world/search and the app's token. */
  search?: GlobalSearchConfig;
  /**
   * Flameco AI (Ask Alpha) for apps that don't mount their own <alpha-chat>: the
   * frame mounts it, and search hands questions to it. Alpha shows it only to
   * people it allows.
   */
  doug?: DougConfig;
  /** Where search sends a question for Flameco AI, when it isn't the Flameco AI widget (Alpha's own chat page). */
  askDoug?: (q: string) => void;
  /** App-specific items in the bar, right of the search (keep it to one small control). */
  topBarSlot?: React.ReactNode;
  /** Extra account-menu entries for this app, shown above Sign out. */
  menuItems?: { key: string; label: string; icon?: React.ReactNode; onClick?: () => void; href?: string }[];
  /** 'touch' = larger controls and rows (shop-floor tablets). */
  density?: 'comfortable' | 'touch';
  /** Local addresses for development, e.g. process.env.NEXT_PUBLIC_FLAMECO_APP_URLS. */
  appUrls?: Partial<Record<FlamecoAppKey, string>> | string | null;
  children: React.ReactNode;
}

const defaultLink = ({ href, className, children, onClick, ...rest }: FlamecoLinkProps) => (
  <a href={href} className={className} onClick={onClick} {...rest}>
    {children}
  </a>
);

/** "Guthrie, James A" -> "James A Guthrie"; already "First Last" stays. */
export function prettyName(name: string): string {
  const i = name.indexOf(',');
  return i > 0 ? `${name.slice(i + 1).trim()} ${name.slice(0, i).trim()}`.trim() : name.trim();
}

export function initials(name: string): string {
  const parts = prettyName(name).split(/\s+/).filter((p) => /^[A-Za-z]/.test(p));
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/** Flameco World's FLAMECO lockup (ink + flame), following light/dark. */
export function FlamecoLockup({ className = 'fl-topbar__lockup' }: { className?: string }) {
  return <svg className={className} viewBox={LOCKUP_VIEWBOX} role="img" aria-label="Flameco" dangerouslySetInnerHTML={{ __html: LOCKUP_SVG }} />;
}

/** The round Flameco mark (ink ring + flame). */
export function FlamecoMark({ className = 'fl-topbar__mark' }: { className?: string }) {
  return <svg className={className} viewBox={MARK_VIEWBOX} aria-hidden="true" dangerouslySetInnerHTML={{ __html: MARK_SVG }} />;
}

/** An icon on a hue-tinted plate, like World's home tiles. hue null = neutral plate. */
export function Plate({ hue, children, small }: { hue?: number | null; children: React.ReactNode; small?: boolean }) {
  const style = hue == null ? undefined : ({ '--fl-hue': hue } as React.CSSProperties);
  return (
    <span className={`fl-plate${small ? ' fl-plate--sm' : ''}`} data-hue={hue == null ? undefined : ''} style={style}>
      {children}
    </span>
  );
}

/**
 * Props that give an element its category colour (bold strip, heading, nav
 * label): a hue, null for the neutral (graphite) category, undefined for none.
 */
export function categoryAttrs(hue: number | null | undefined): { 'data-fl-cat'?: string; style?: React.CSSProperties } {
  if (hue === undefined) return {};
  if (hue === null) return { 'data-fl-cat': 'neutral' };
  return { 'data-fl-cat': '', style: { '--fl-cat-hue': hue } as React.CSSProperties };
}

/** Closes a popover on outside click or Escape. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

function useTheme(): FlamecoTheme {
  const [theme, setThemeState] = useState<FlamecoTheme>('light');
  useEffect(() => {
    setThemeState(getTheme());
    return onThemeChange(setThemeState);
  }, []);
  return theme;
}

export function AppSwitcher({ current, apps }: { current: FlamecoAppKey; apps: FlamecoApp[] }) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="fl-pop-anchor" ref={ref}>
      <button type="button" className="fl-iconbtn" aria-label="Flameco apps" title="Flameco apps" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        <IconGrid className="fl-icon fl-icon--sm" />
      </button>
      {open && (
        <div className="fl-pop fl-switcher" role="menu">
          <div className="fl-switcher__title">Flameco apps</div>
          <div className="fl-switcher__grid">
            {apps.map((a) => {
              const Icon = APP_ICONS[a.key];
              return (
                <a key={a.key} href={a.url} className="fl-switcher__app" role="menuitem" aria-current={a.key === current ? 'true' : undefined}>
                  <Plate hue={a.hue} small>
                    {Icon ? <Icon /> : null}
                  </Plate>
                  <span>
                    <span className="fl-switcher__name">{a.short}</span>
                    {a.onNetwork && <span className="fl-switcher__note">On network</span>}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function ThemeToggle() {
  const theme = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      className="fl-iconbtn"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {dark ? <IconSun className="fl-icon fl-icon--sm" /> : <IconMoon className="fl-icon fl-icon--sm" />}
    </button>
  );
}

export function AccountMenu({
  user,
  onSignOut,
  worldUrl,
  menuItems,
  renderLink = defaultLink,
}: {
  user: FlamecoFrameUser;
  onSignOut: () => void;
  worldUrl: string;
  menuItems?: FlamecoFrameProps['menuItems'];
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
}) {
  const { open, setOpen, ref } = usePopover();
  const theme = useTheme();
  const name = prettyName(user.name);
  const meta = [user.role, user.employeeId ? `#${user.employeeId}` : null].filter(Boolean).join(' · ');
  return (
    <div className="fl-pop-anchor" ref={ref}>
      <button type="button" className="fl-avatar-btn" aria-haspopup="true" aria-expanded={open} aria-label={`Account: ${name}`} title={name} onClick={() => setOpen(!open)}>
        <span className="fl-avatar">{initials(user.name)}</span>
      </button>
      {open && (
        <div className="fl-pop fl-menu" role="menu">
          <div className="fl-menu__who">
            <span className="fl-avatar fl-avatar--lg">{initials(user.name)}</span>
            <div style={{ minWidth: 0 }}>
              <div className="fl-menu__name">{name}</div>
              {meta && <div className="fl-menu__meta">{meta}</div>}
            </div>
          </div>
          <div className="fl-menu__label">Appearance</div>
          <div className="fl-seg">
            <button type="button" aria-pressed={theme === 'light'} onClick={() => setTheme('light')}>
              <IconSun className="fl-icon fl-icon--sm" /> Light
            </button>
            <button type="button" aria-pressed={theme === 'dark'} onClick={() => setTheme('dark')}>
              <IconMoon className="fl-icon fl-icon--sm" /> Dark
            </button>
          </div>
          <div className="fl-menu__sep" />
          <div className="fl-menu__body">
            <a className="fl-menu__item" role="menuitem" href={worldProfileUrl(user.employeeId ?? '', worldUrl)}>
              <IconUser /> People in World
            </a>
            {menuItems?.map((m) =>
              m.href ? (
                <React.Fragment key={m.key}>
                  {renderLink({
                    href: m.href,
                    className: 'fl-menu__item',
                    onClick: () => setOpen(false),
                    children: (
                      <>
                        {m.icon} {m.label}
                      </>
                    ),
                  })}
                </React.Fragment>
              ) : (
                <button
                  key={m.key}
                  type="button"
                  className="fl-menu__item"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    m.onClick?.();
                  }}
                >
                  {m.icon} {m.label}
                </button>
              ),
            )}
            <button type="button" className="fl-menu__item fl-menu__item--danger" role="menuitem" onClick={onSignOut}>
              <IconLogOut /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function isActive(item: FlamecoNavItem, currentPath?: string): boolean {
  if (item.active !== undefined) return item.active;
  if (!item.href || !currentPath) return false;
  const href = item.href.split(/[?#]/)[0];
  if (href === '/') return currentPath === '/';
  return currentPath === href || currentPath.startsWith(href.replace(/\/+$/, '') + '/');
}

export function SideNav({
  nav,
  currentPath,
  renderLink = defaultLink,
  onNavigate,
}: {
  nav: FlamecoNavGroup[];
  currentPath?: string;
  renderLink?: (p: FlamecoLinkProps) => React.ReactNode;
  onNavigate?: () => void;
}) {
  return (
    <nav className="fl-nav" aria-label="Main">
      {nav.map((g, gi) => (
        <div className="fl-nav__group" key={g.section ?? gi} {...categoryAttrs(g.categoryHue)}>
          {g.section && <div className="fl-nav__section">{g.section}</div>}
          {g.items.map((item) => {
            const active = isActive(item, currentPath);
            const inner = (
              <>
                {item.icon && <span className="fl-nav__icon">{item.icon}</span>}
                <span className="fl-nav__label">{item.label}</span>
                {item.badge != null && item.badge !== false && <span className="fl-nav__badge">{item.badge}</span>}
              </>
            );
            if (item.href) {
              return (
                <React.Fragment key={item.key}>
                  {renderLink({
                    href: item.href,
                    className: 'fl-nav__item',
                    'aria-current': active ? 'page' : undefined,
                    onClick: () => {
                      item.onClick?.();
                      onNavigate?.();
                    },
                    children: inner,
                  })}
                </React.Fragment>
              );
            }
            return (
              <button
                key={item.key}
                type="button"
                className="fl-nav__item"
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  item.onClick?.();
                  onNavigate?.();
                }}
              >
                {inner}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export function TopBar({
  app,
  user,
  onSignOut,
  renderLink = defaultLink,
  navigate,
  search,
  homeHref,
  askDoug,
  topBarSlot,
  menuItems,
  appUrls,
  onMenu,
  menuOpen,
}: Omit<FlamecoFrameProps, 'children' | 'nav' | 'currentPath' | 'density' | 'doug'> & { onMenu?: () => void; menuOpen?: boolean }) {
  const apps = useMemo(() => resolveApps(appUrls), [appUrls]);
  const me = apps.find((a) => a.key === app) ?? FLAMECO_APPS[0];
  const worldUrl = apps.find((a) => a.key === 'world')?.url ?? 'https://world.flameco.com/';
  const [searchOpen, setSearchOpen] = useState(false);
  const [listen, setListen] = useState(false);
  const openSearch = useCallback(() => {
    setListen(false);
    setSearchOpen(true);
  }, []);
  const openVoice = useCallback(() => {
    setListen(true);
    setSearchOpen(true);
  }, []);
  useSearchShortcut(openSearch);
  const brand = (
    <>
      <FlamecoLockup />
      <FlamecoMark />
      <span className="fl-topbar__divider" aria-hidden="true" />
      <span className="fl-topbar__app">{me.name}</span>
    </>
  );
  return (
    <header className="fl-topbar">
      {onMenu && (
        <button type="button" className="fl-iconbtn fl-topbar__menu" aria-label="Menu" aria-expanded={!!menuOpen} onClick={onMenu}>
          <IconMenu className="fl-icon fl-icon--sm" />
        </button>
      )}
      {/* The logo opens the app's own home when it gives one (homeHref, MFG); otherwise Flameco World's home. */}
      {homeHref ? (
        renderLink({ href: homeHref, className: 'fl-topbar__brand', children: brand })
      ) : (
        <a className="fl-topbar__brand" href={worldUrl} title="Flameco World home">
          {brand}
        </a>
      )}
      <SearchButton onOpen={openSearch} onVoice={openVoice} config={search} />
      {topBarSlot && <div className="fl-topbar__slot">{topBarSlot}</div>}
      <div className="fl-topbar__end">
        <AppSwitcher current={app} apps={apps} />
        <ThemeToggle />
        {user && <AccountMenu user={user} onSignOut={onSignOut} worldUrl={worldUrl} menuItems={menuItems} renderLink={renderLink} />}
      </div>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} apps={apps} current={app} config={search} navigate={navigate} onAskDoug={askDoug} listen={listen} />
    </header>
  );
}

export function FlamecoFrame(props: FlamecoFrameProps) {
  const { nav, currentPath, renderLink, density = 'comfortable', children } = props;
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    initTheme();
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);
  // A route change closes the phone/tablet drawer.
  useEffect(() => setNavOpen(false), [currentPath]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);
  const close = useCallback(() => setNavOpen(false), []);
  const hasNav = !!nav && nav.some((g) => g.items.length > 0);
  return (
    <div className="fl-frame" data-nav-open={navOpen ? 'true' : 'false'}>
      <TopBar {...props} onMenu={hasNav ? () => setNavOpen((o) => !o) : undefined} menuOpen={navOpen} />
      <div className="fl-frame__body">
        {hasNav && <SideNav nav={nav!} currentPath={currentPath} renderLink={renderLink} onNavigate={close} />}
        {hasNav && <div className="fl-scrim" onClick={close} aria-hidden="true" />}
        <main className="fl-frame__main">{children}</main>
      </div>
      {props.doug && props.user && <DougWidget config={{ ...props.doug, currentPath: props.doug.currentPath ?? currentPath }} />}
    </div>
  );
}
